import axios from "axios";
import configData from "../config.json";
import { Exception } from "node-sass";
import { TimerSharp } from "@mui/icons-material";

export class VirtualLibrarianService {
  constructor(globalAuthState) {
    this._urlBase = configData.VIRTUAL_LIBRARIAN.SERVICE_URL;
    this._urlBaseApi = configData.API_URIS.CORE;

    const authState = globalAuthState.get();
    this._globalAuthState = globalAuthState;
    if (
      authState?.isAuthenticated &&
      authState?.accessToken != null &&
      authState.idToken != null
    ) {
      this._clientId = authState.idTokenClaims.clientId;
      this._isAuth = true;
    } else {
      this._isAuth = false;
    }

    this._url = this._urlBase;
    axios.defaults.headers.post["Content-Type"] =
      "application/json; charset=utf-8";
  }

  _createAxiosInstance() {
    const authState = this._globalAuthState.get();
    const token = authState?.accessToken;

    let headers = {};
    if (token) {
      headers["Authorization"] = "Bearer " + token;
    }

    const instance = axios.create({
      timeout: this.timeout,
      headers: headers,
    });
    return instance;
  }

  _createAxiosError(e) {
    return e.response && e.response?.data !== ""
      ? e.response.data
      : [
          {
            Exception: null,
            Message: {
              DisplayMessage: e.message,
              Code: "JSE1",
              Info: e.stack,
              IsFailMessage: true,
              Name: "JS_ERROR",
            },
            InnerResults: [],
            Items: [],
            EventPersisted: false,
            EventDefinitionId: "SYSTEM",
            EventId: "00000000-0000-0000-0000-000000000000",
            CurrentPage: 0,
            TotalItemCount: 0,
            ItemsPerPage: 0,
            MessageDetails: e.stack,
            Success: false,
          },
        ];
  }

  async getVirtualLibrarianGuardrails() {
    const instance = this._createAxiosInstance();
    const url = `${this._url}systemPrompts`;
    try {
      const response = await instance.get(url);
      return response.data;
    } catch (error) {
      throw new Exception(
        "Error fetching ECHO Enhanced Search guardrails: " + error.message
      );
    }
  }

  async getVirtualLibrarianGuardrail(id) {
    const instance = this._createAxiosInstance();
    const url = `${this._url}systemPrompts/${id}`;
    return instance
      .get(url)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async createGuardrail(data) {
    const instance = this._createAxiosInstance();
    const url = `${this._url}systemPrompts`;
    return instance
      .post(url, data)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async saveGuardrail(id, data) {
    const instance = this._createAxiosInstance();
    const url = `${this._url}systemPrompts/${id}`;
    return instance
      .put(url, data)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async deleteVirtualLibrarianGuardrail(id) {
    const instance = this._createAxiosInstance();
    const url = `${this._url}systemPrompts/${id}`;
    return instance
      .delete(url)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async getVirtualLibrarianSystemPrompt() {
    const instance = this._createAxiosInstance();
    const url = `${this._url}systemPrompt`;
    return instance
      .get(url)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async askQuestion(data) {
    const instance = this._createAxiosInstance();
    const url = `${this._url}ask`;
    return instance
      .post(url, data)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async getContent(contentIds) {
    const instance = this._createAxiosInstance();
    const url = `${this._url}contentById`;
    return instance
      .post(url, contentIds)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async getSasTokenedContent(contentId) {
    const instance = this._createAxiosInstance();

    const url = `${this._url}contentWithSas/${contentId}`;
    return instance
      .get(url)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async getAllChatLogs() {
    const instance = this._createAxiosInstance();
    const url = `${this._url}chatLogs`;
    return instance
      .get(url)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async getChatLogsById() {
    const instance = this._createAxiosInstance();
    const url = `${this._url}chatLogsByUserId`;
    return instance
      .get(url)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async getAvailableContent() {
    const instance = this._createAxiosInstance();
    const url = `${this._url}searchContent`;
    return instance
      .post(url, {})
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  ///////
  async getVirtualLibrarianSettings() {
    const instance = this._createAxiosInstance();
    const url = `${this._url}vlconfig`;
    return instance
      .get(url)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

  async updateVirtualLibrarianSettings(setting) {
    const instance = this._createAxiosInstance();
    const url = `${this._url}vlconfig`;
    return instance
      .put(url, setting)
      .then((r) => {
        return { Success: true, Data: r.data };
      })
      .catch((e) => {
        return {
          Success: false,
          DisplayMessage: e.message,
          Data: this._createAxiosError(e),
        };
      });
  }

}
